import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { getMyLeagues, rememberLeague, lastLeague } from "../lib/league";
import { landingTarget } from "../shared/landing";
import { syncCurrentDevice } from "../lib/push";
import { NoCrews } from "./CrewLayout";

// The bare "/" route: work out which crew to open, then get out of the way.
//
// This used to live inside Home, which meant Home was doing two unrelated jobs:
// deciding which crew you are in, and drawing it. Splitting them is what lets
// every crew screen share one answer to the first question.
export function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;

      // Quietly make sure the server is pointed at this device. A push id that
      // rotated while the app was closed would otherwise leave someone silently
      // unreachable while the app still told them notifications were on.
      syncCurrentDevice();

      // Claim any invite waiting on this email, every time, not only when they
      // belong to nothing. Someone already in one crew who gets invited to a
      // second is the normal case, and gating this on "has no crew" meant
      // signing in with the invited address quietly did nothing.
      // The link they actually tapped, if there was one. Without it the server
      // can only guess which crew they meant when several invites are waiting.
      const tapped = localStorage.getItem("pending_invite");
      let claimed: string | null = null;
      try {
        const r = await api<{ leagueId: string | null; joined: string[] }>(
          `/api/join`, { body: { op: "auto", preferToken: tapped } }
        );
        claimed = r.leagueId;
        if (r.joined?.length) localStorage.removeItem("pending_invite");
      } catch { /* not fatal, they may already be joined */ }

      const crews = await getMyLeagues(user.id);
      if (!alive) return;

      const target = landingTarget({
        claimedLeagueId: claimed,
        pendingToken: localStorage.getItem("pending_invite"),
        crewIds: crews.map((c) => c.id),
        rememberedId: lastLeague(),
      });

      if (target.kind === "onboarding") {
        rememberLeague(target.leagueId);
        navigate(`/league/${target.leagueId}/onboarding`, { replace: true });
        return;
      }
      // A link they tapped and have not used yet. Redeeming it is a real join
      // against that crew, so this goes through the invite screen rather than
      // guessing here.
      if (target.kind === "redeem") {
        navigate(`/join/${target.token}`, { replace: true });
        return;
      }
      if (target.kind === "crew") {
        rememberLeague(target.leagueId);
        navigate(`/league/${target.leagueId}`, { replace: true });
        return;
      }
      setEmpty(true);
    })();
    return () => { alive = false; };
  }, [user, navigate]);

  if (empty) return <NoCrews />;
  return <div className="app"><div className="loading">Loading…</div></div>;
}
